import { useTranslation } from "react-i18next";
import { useParams } from "react-router";

import {
  DEFAULT_LANGUAGE,
  getLanguageByCode,
  isSupportedLanguage,
  type LanguageCode,
} from "../lib/i18n";
import type { Language } from "../lib/i18n/languages";

type UseLocale = {
  /** Active language object. */
  language: Language;
  /** Active language code (always one of `SUPPORTED_LANGUAGES`). */
  code: LanguageCode;
  /** Convenience flag — true for Arabic, false otherwise. */
  isRTL: boolean;
  /** Build a path scoped to the current language (e.g. `pathFor("/privacy")`). */
  pathFor: (path: string) => string;
};

/**
 * Reads the `:lang` URL segment, validates it, falls back to the i18next
 * detected language if missing or invalid, and returns a typed locale handle.
 */
export function useLocale(): UseLocale {
  const params = useParams<{ lang?: string }>();
  const { i18n } = useTranslation();

  const raw = params.lang ?? i18n.resolvedLanguage ?? i18n.language;
  const code: LanguageCode = isSupportedLanguage(raw)
    ? raw
    : DEFAULT_LANGUAGE;
  const language = getLanguageByCode(code);

  const pathFor = (path: string): string => {
    if (path.startsWith("http") || path.startsWith("mailto:")) return path;
    if (path.startsWith("#")) return `/${code}${path}`;
    const normalized = path.startsWith("/") ? path : `/${path}`;
    return `/${code}${normalized === "/" ? "" : normalized}`;
  };

  return { language, code, isRTL: language.rtl, pathFor };
}
